import { Link } from 'react-router-dom';
import { FaInfoCircle, FaCopy, FaHistory } from 'react-icons/fa';
import NewTooltip from '../../../atoms/NewTooltip';
import { CreditAnalysis } from '../types';
import { ColumnDef } from '../components/DataTable.types';
import { getRatingColor, fmtCurrency, fmtPercent, fmtMil, financialBg } from './analysisFormatters';
import EditableDecision from './EditableDecision';
import EditableTextField from './EditableTextField';
import EditableApprovedValue from './EditableApprovedValue';

interface DecisionPayload {
    status: string;
    approvedValue?: number;
    justification?: string;
    creditObs?: string;
    cprNumber?: string;
    decidedBy?: string;
}

interface ColumnCallbacks {
    onOpenModal: (row: CreditAnalysis) => void;
    onOpenDrawee: (doc: string, itemId: string, name: string, batchId: string) => void;
    onSaveDecision: (batchId: string, uuid: string, data: DecisionPayload) => Promise<any>;
    onUpdateRow: (uuid: string, updates: Partial<CreditAnalysis>) => void;
    featureFlags: Record<string, boolean>;
    hideBatchColumn?: boolean;
}

export function buildAnalysisColumns(cb: ColumnCallbacks): ColumnDef<CreditAnalysis>[] {
    return [
        {
            id: 'col-actions', field: 'actions', title: 'Ações', width: '80px', canSort: false, sticky: true,
            renderCell: (row) => (
                <div className="flex items-center justify-center gap-2">
                    {cb.featureFlags['credit-analysis:approve'] !== false && (
                        <NewTooltip content="Informações de Consulta">
                            <button onClick={() => cb.onOpenModal(row)} className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                                <FaInfoCircle className="text-lg" />
                            </button>
                        </NewTooltip>
                    )}
                    <NewTooltip content="Histórico do Sacado">
                        <a href={`/#/credit-analysis/historico?document=${encodeURIComponent(row.document)}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                            <FaHistory className="text-sm" />
                        </a>
                    </NewTooltip>
                </div>
            ),
        },
        ...(!cb.hideBatchColumn ? [{
            id: 'col-1', field: 'batchNumber', title: 'Lote', width: '150px', canSort: true, sticky: true,
            renderCell: (row: CreditAnalysis) => row.batchNumber
                ? <Link to={`/credit-analysis/batch/${row.id}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium">{row.batchNumber}</Link>
                : <span>-</span>,
        }] : []),
        {
            id: 'col-2', field: 'drawee', title: 'Sacado', width: '200px', canSort: true, sticky: true,
            renderCell: (row) => <span className="dark:text-gray-200 block max-w-[200px] truncate" title={row.drawee}>{row.drawee}</span>,
        },
        {
            id: 'col-3', field: 'document', title: 'CPF/CNPJ', width: '170px', canSort: true, sticky: true,
            renderCell: (row) => (
                <button
                    onClick={() => cb.onOpenDrawee(row.document, row.uuid, row.drawee, row.id)}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline font-mono"
                >
                    {row.document}
                </button>
            ),
        },
        ...(!cb.hideBatchColumn ? [{ id: 'col-4', field: 'date', title: 'Data', width: '120px', canSort: true }] : []),
        { id: 'col-5', field: 'assignor', title: 'Cedente', width: '150px', canSort: true, renderCell: (row) => <span className="dark:text-gray-200 block max-w-[150px] truncate" title={row.assignor}>{row.assignor}</span> },
        { id: 'col-6', field: 'type', title: 'Tipo', width: '100px', canSort: true, align: 'center' as const, filterType: 'select' as const, filterOptions: [{ value: 'PF', label: 'PF' }, { value: 'PJ', label: 'PJ' }] },
        {
            id: 'col-7', field: 'year', title: 'Ano', width: '100px', canSort: true,
            renderCell: (row) => row.year
                ? <span className={`block px-2 py-1 rounded font-medium text-center ${parseInt(row.year) > 2024 ? 'bg-[#ea9999] text-white' : 'dark:text-gray-200'}`}>{row.year}</span>
                : <span>-</span>,
        },
        { id: 'col-8', field: 'city', title: 'Cidade', width: '150px', canSort: true },
        { id: 'col-9', field: 'state', title: 'UF', width: '80px', canSort: true, align: 'center' as const },
        { id: 'col-9b', field: 'economicGroup', title: 'Grupo Econômico', width: '180px', canSort: true },
        {
            id: 'col-10', field: 'qsa', filterType: 'range' as const, title: 'QSA (R$ mil)', width: '120px', canSort: true,
            renderCell: (row) => row.qsa
                ? <span className={`block px-2 py-1 rounded font-medium text-right ${parseFloat(row.qsa) < 100 ? 'bg-[#ea9999] text-white' : parseFloat(row.qsa) <= 1000 ? 'bg-yellow-400 text-gray-900' : 'dark:text-gray-200'}`}>{fmtMil(row.qsa)}</span>
                : <span>-</span>,
        },
        { id: 'col-13', field: 'annualRevenue', filterType: 'range' as const, title: 'Rec. Anual (R$ mil)', width: '150px', canSort: true, align: 'right' as const, bgClassName: financialBg, renderCell: (row) => <span className="dark:text-gray-200">{fmtMil(row.annualRevenue)}</span> },
        {
            id: 'col-14', field: 'equity', filterType: 'range' as const, title: 'PL (R$ mil)', width: '120px', canSort: true, bgClassName: financialBg,
            renderCell: (row) => row.equity
                ? <span className={`block px-2 py-1 rounded font-medium text-right ${parseFloat(row.equity) < 0 ? 'bg-[#ea9999] text-white' : 'dark:text-gray-200'}`}>{fmtMil(row.equity)}</span>
                : <span>-</span>,
        },
        {
            id: 'col-15', field: 'rating', title: 'Rating', width: '100px', canSort: true, align: 'center' as const,
            filterType: 'multiselect' as const,
            filterOptions: ['AA+', 'AA', 'AA-', 'AX', 'A+', 'A', 'A-', 'BX', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'EX', 'E+', 'E', 'E-', 'F', 'TBD'].map(v => ({ value: v, label: v })),
            renderCell: (row) => {
                const c = getRatingColor(row.rating);
                return <span className={`px-2 py-1 rounded font-medium ${c.bg} ${c.text}`}>{row.rating || '-'}</span>;
            },
        },
        {
            id: 'col-16', field: 'recommendation', title: 'Recomendação', width: '140px', canSort: true,
            filterType: 'multiselect' as const,
            filterOptions: [{ value: 'APPROVED', label: 'Aprovado' }, { value: 'REPROVADO', label: 'Reprovado' }, { value: 'PENDENTE', label: 'Pendente' }],
            renderCell: (row) => {
                const r = (row.recommendation || '').toUpperCase();
                const isApproved = r === 'APPROVED' || r === 'APROVADO';
                const isRejected = r === 'REJECTED' || r === 'REPROVADO';
                const isPending = r === 'PENDING' || r === 'PENDENTE';
                const label = isApproved ? 'Aprovado' : isRejected ? 'Reprovado' : isPending ? 'Pendente' : row.recommendation || '-';
                const cls = isApproved ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                    : isRejected ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                        : isPending ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
                return <span className={`block px-2 py-1 rounded text-xs font-medium text-center ${cls}`}>{label}</span>;
            },
        },
        {
            id: 'col-17', field: 'status', title: 'Decisão', width: '120px', canSort: true,
            filterType: 'multiselect' as const,
            filterOptions: [{ value: 'PENDING', label: 'Pendente' }, { value: 'APPROVED', label: 'Aprovado' }, { value: 'REJECTED', label: 'Reprovado' }, { value: 'PRE_APPROVED', label: 'Pré-aprovado' }, { value: 'CANCELED', label: 'Cancelado' }],
            renderCell: (row) => (
                <EditableDecision row={row} onSave={async (uuid, status) => {
                    await cb.onSaveDecision(row.id, uuid, { status });
                    cb.onUpdateRow(uuid, { status });
                }} />
            ),
        },
        {
            id: 'col-17b', field: 'suggestedValue', title: 'Valor Sugerido (R$)', width: '170px', canSort: true, align: 'right' as const,
            renderCell: (row) => (
                <div className="flex items-center justify-end gap-1">
                    <span className="dark:text-gray-200">{fmtCurrency(row.suggestedValue)}</span>
                    {parseFloat(row.suggestedValue) > 0 && (
                        <button
                            onClick={async () => {
                                const num = parseFloat(row.suggestedValue);
                                await cb.onSaveDecision(row.id, row.uuid, { status: 'APPROVED', approvedValue: num });
                                cb.onUpdateRow(row.uuid, { approvedValue: String(num), status: 'APPROVED' });
                            }}
                            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                            title="Aprovar com valor sugerido"
                        >
                            <FaCopy className="text-sm" />
                        </button>
                    )}
                </div>
            ),
        },
        {
            id: 'col-18', field: 'approvedValue', title: 'Valor Aprovado (R$)', width: '150px', canSort: true, align: 'right' as const,
            renderCell: (row) => (
                <EditableApprovedValue row={row} onSave={async (uuid, num) => {
                    await cb.onSaveDecision(row.id, uuid, { status: row.status || 'PENDING', approvedValue: num });
                    cb.onUpdateRow(uuid, { approvedValue: String(num) });
                }} />
            ),
        },
        { id: 'col-19', field: 'approvedPercent', title: '%Aprov/SCR', width: '130px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtPercent(row.approvedPercent)}</span> },
        {
            id: 'col-20', field: 'justification', title: 'Justificativa', width: '200px', canSort: false,
            renderCell: (row) => (
                <EditableTextField value={row.justification} maxLength={500} onSave={async (val) => {
                    await cb.onSaveDecision(row.id, row.uuid, { status: row.status || 'PENDING', justification: val });
                    cb.onUpdateRow(row.uuid, { justification: val });
                }} />
            ),
        },
        { id: 'col-21', field: 'originalValue', filterType: 'range' as const, title: 'Vlr Original (R$)', width: '150px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtCurrency(row.originalValue)}</span> },
        { id: 'col-22', field: 'dupPercent', title: '% Dup/SCR', width: '120px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtPercent(row.dupPercent)}</span> },
        {
            id: 'col-23', field: 'creditObs', title: 'Obs Crédito', width: '200px', canSort: false,
            renderCell: (row) => (
                <EditableTextField value={row.creditObs} maxLength={500} onSave={async (val) => {
                    await cb.onSaveDecision(row.id, row.uuid, { status: row.status || 'PENDING', creditObs: val });
                    cb.onUpdateRow(row.uuid, { creditObs: val });
                }} />
            ),
        },
        { id: 'col-24', field: 'currentExposure', filterType: 'range' as const, title: 'Exposição Atual', width: '130px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtCurrency(row.currentExposure)}</span> },
        { id: 'col-25', field: 'potentialExposure', filterType: 'range' as const, title: 'Exposição Potencial', width: '150px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtCurrency(row.potentialExposure)}</span> },
        { id: 'col-26', field: 'scrTotal', filterType: 'range' as const, title: 'SCR Total (R$ mil)', width: '130px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtMil(row.scrTotal)}</span> },
        { id: 'col-27', field: 'scrRural', filterType: 'range' as const, title: 'SCR Rural (R$ mil)', width: '130px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtMil(row.scrRural)}</span> },
        { id: 'col-28', field: 'overdue', filterType: 'range' as const, title: 'Vencido (R$ mil)', width: '120px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{fmtMil(row.overdue)}</span> },
        { id: 'col-29', field: 'score', filterType: 'range' as const, title: 'Score', width: '120px', canSort: true, align: 'center' as const, renderCell: (row) => <span className="dark:text-gray-200">{row.score && parseFloat(row.score) !== 0 ? row.score : row.score === '0' || row.score === '0.0' ? 'Não calculado' : '-'}</span> },
        { id: 'col-30', field: 'pdef', filterType: 'range' as const, title: 'Pdef', width: '120px', canSort: true, align: 'center' as const, renderCell: (row) => <span className="dark:text-gray-200">{row.score && parseFloat(row.score) === 0 ? 'Não calculado' : row.pdef ? `${parseFloat(row.pdef).toFixed(2)}%` : '-'}</span> },
        { id: 'col-31', field: 'scoreQuod', filterType: 'range' as const, title: 'Score Quod', width: '110px', canSort: true, align: 'center' as const },
        {
            id: 'col-32', field: 'restriction', title: 'Restrições', width: '200px', canSort: true,
            renderCell: (row) => {
                const r = (row as any).restrictions || [];
                if (!r.length) return <span className="text-gray-400">-</span>;
                const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
                return (
                    <div className="text-xs leading-tight">
                        {r.map((item: any, i: number) => (
                            <div key={i} className="text-red-700 dark:text-red-400">
                                {item.type}: {item.count}x ({fmt(item.balance)})
                            </div>
                        ))}
                    </div>
                );
            },
        },
        { id: 'col-ccir-ha', field: 'ccirHa', filterType: 'range' as const, title: 'CCIR ha', width: '100px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{(row as any).ccirHa || '-'}</span> },
        { id: 'col-ccir-val', field: 'ccirValue', filterType: 'range' as const, title: 'CCIR R$ (mil)', width: '120px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{(row as any).ccirValue ? fmtMil((row as any).ccirValue) : '-'}</span> },
        { id: 'col-patrim-scr', field: 'patrimonioScr', filterType: 'range' as const, title: 'Patrim/SCR %', width: '110px', canSort: true, align: 'right' as const, renderCell: (row) => <span className="dark:text-gray-200">{(row as any).patrimonioScr ? `${(row as any).patrimonioScr}%` : '-'}</span> },
        {
            id: 'col-33', field: 'cprNumber', title: 'Nº do Contrato', width: '120px', canSort: true,
            renderCell: (row) => (
                <EditableTextField value={row.cprNumber} maxLength={20} onSave={async (val) => {
                    await cb.onSaveDecision(row.id, row.uuid, { status: row.status || 'PENDING', cprNumber: val });
                    cb.onUpdateRow(row.uuid, { cprNumber: val });
                }} />
            ),
        },
    ];
}
